import { useEffect, useState, useCallback } from 'react';
import Layout from '@/components/Layout';
import { BookOpen, Download, RefreshCw, Wallet } from 'lucide-react';
import axios from 'axios';
import LedgerStatementTable, { fmtVES, ENTRY_TYPES } from '@/components/LedgerStatementTable';
import { downloadAuthedFile } from '@/lib/downloadFile';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

export default function LedgerStatement() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [range, setRange] = useState({ start: '', end: '' });
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const exportCsv = async () => {
    setExporting(true);
    setExportError('');
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.set('entry_type', typeFilter);
      if (range.start) params.set('start', `${range.start}T00:00:00`);
      if (range.end) params.set('end', `${range.end}T23:59:59`);
      const qs = params.toString();
      await downloadAuthedFile(
        `${BACKEND_URL}/api/merchant/ledger/export${qs ? `?${qs}` : ''}`,
        'estado_cuenta.csv',
      );
    } catch (e) {
      setExportError('No se pudo generar el archivo. Intenta de nuevo.');
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset });
      if (typeFilter) params.set('entry_type', typeFilter);
      const res = await axios.get(`${BACKEND_URL}/api/merchant/ledger?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [offset, typeFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <Layout>
      <div className="space-y-6" data-testid="merchant-ledger-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-blue-700" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Estado de Cuenta</h1>
              <p className="text-sm text-gray-500">Hoja auditable de tu saldo: pagos, fees, comisiones y dispersiones</p>
            </div>
          </div>
          <button
            data-testid="ledger-refresh-btn"
            onClick={load}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" /> Actualizar
          </button>
        </div>

        <div className="bg-white rounded-xl border p-4 flex items-end gap-3 flex-wrap">
          <label className="text-xs text-gray-500">
            Desde
            <input
              type="date" data-testid="ledger-export-start"
              value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })}
              className="block border rounded-lg px-2 py-1.5 text-sm text-gray-800"
            />
          </label>
          <label className="text-xs text-gray-500">
            Hasta
            <input
              type="date" data-testid="ledger-export-end"
              value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })}
              className="block border rounded-lg px-2 py-1.5 text-sm text-gray-800"
            />
          </label>
          <button
            data-testid="ledger-export-csv-btn"
            onClick={exportCsv}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> {exporting ? 'Generando…' : 'Exportar CSV'}
          </button>
          <p className="text-xs text-gray-400 flex-1 min-w-[200px]">
            Descarga todos tus movimientos (no solo esta página) en un archivo que abre directo en Excel.
          </p>
          {exportError && (
            <p className="text-xs text-red-600 w-full" data-testid="ledger-export-error">{exportError}</p>
          )}
        </div>

        <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-xl p-6 text-white flex items-center gap-4">
          <div className="p-3 bg-white/10 rounded-full"><Wallet className="w-8 h-8" /></div>
          <div>
            <p className="text-sm text-blue-200">Saldo disponible</p>
            <p className="text-3xl font-bold" data-testid="ledger-balance">{data ? fmtVES(data.balance) : '…'}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Movimientos</h2>
            <select
              data-testid="ledger-type-filter"
              value={typeFilter}
              onChange={(e) => { setOffset(0); setTypeFilter(e.target.value); }}
              className="border rounded-lg px-3 py-1.5 text-sm"
            >
              <option value="">Todos los tipos</option>
              {Object.entries(ENTRY_TYPES).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          {loading ? (
            <div className="text-center py-10 text-gray-400">Cargando…</div>
          ) : (
            <>
              <LedgerStatementTable entries={data?.entries} />
              <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
                <span>{data?.total_entries ?? 0} asientos en total</span>
                <div className="flex gap-2">
                  <button
                    data-testid="ledger-prev-page"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    className="px-3 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
                  >Anterior</button>
                  <button
                    data-testid="ledger-next-page"
                    disabled={!data || offset + PAGE_SIZE >= (data.entries?.length < PAGE_SIZE ? offset + PAGE_SIZE : data.total_entries)}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                    className="px-3 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
                  >Siguiente</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
